import { ModalPortalContext } from '@ardoq/hooks';
import { colors } from '@ardoq/design-tokens';
import { createTopLayerHost, hideTopLayerHost } from '@ardoq/dom-utils';
import { useFocusFirstElement, useKeydown } from '@ardoq/hooks';
import { FocusScope } from '@react-aria/focus';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { handleEscapeButtonPressed } from '../utils/ModalContainer';
import {
  TransitionState,
  transitionDelay,
  transitionDuration,
  useTransitionState,
} from '../utils/useTransitionState';
import { useLastOpenChildren } from '../utils/useLastOpenChildren';
import { MODAL_BODY_ID } from './ModalBody';
import { MODAL_HEADER_ID, MODAL_HEADER_TITLE_ID } from './ModalHeader';

type AnimatedModalDialogProps = {
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
  onClickOutside?: VoidFunction;
  onEscPress?: VoidFunction;
};

/**
 * CSS selector for elements within the modal header. Used to exclude the close button from focus.
 */
const MODAL_HEADER_EXCLUDE_SELECTOR = `#${MODAL_HEADER_ID} *`;

/**
 * Animated modal dialog component that handles opening and closing animations
 * for both the backdrop and the dialog itself.
 *
 * Focus will be automatically set to the first focusable element within the dialog
 * when the dialog is opened, and it will restore focus to the previously focused element
 * when the dialog is closed.
 *
 * To override the default focus behavior, you can add the `data-autofocus` attribute
 * to the element you want to focus.
 *
 * Dropdowns rendered inside this dialog via `OptionalPortal` are automatically portaled
 * into a container within the FocusScope, so focus remains trapped in the dialog while
 * the dropdown is open.
 */
export const AnimatedModalDialog = (props: AnimatedModalDialogProps) => {
  /**
   * We need two transition states as timings may differ
   */
  const [backdropState, updateBackdropState] = useTransitionState(props.isOpen);
  const [dialogState, updateDialogState] = useTransitionState(props.isOpen);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );

  const containerSelector = String(AnimatedDialog);

  useTopmostModalEscapeKey(props.onEscPress, containerSelector, dialogRef);

  useFocusFirstElement({
    containerRef: dialogRef,
    shouldFocus: dialogState === 'entering',
    excludeSelector: MODAL_HEADER_EXCLUDE_SELECTOR,
  });

  const hasExited =
    !props.isOpen && backdropState === 'exited' && dialogState === 'exited';

  const children = useLastOpenChildren(props.isOpen, props.children, hasExited);

  /**
   * Do not render anything if modal is closed and is not playing any exit animations
   */
  if (hasExited) {
    return null;
  }

  return (
    <Portal>
      <Overlay $state={dialogState} onClick={props.onClickOutside} />
      <AnimatedBackdrop
        $state={backdropState}
        onAnimationEnd={updateBackdropState}
        onTransitionEnd={updateBackdropState}
      />
      <CenteredDialog>
        <FocusScope contain restoreFocus autoFocus>
          <AnimatedDialog
            ref={dialogRef}
            $state={dialogState}
            className={props.className}
            inert={!props.isOpen}
            onAnimationEnd={updateDialogState}
            onTransitionEnd={updateDialogState}
          >
            <ModalPortalContext.Provider value={portalContainer}>
              {children}
            </ModalPortalContext.Provider>
          </AnimatedDialog>
          {/*
           * Portal target for dropdowns inside this dialog. Placed inside the FocusScope
           * so that focus stays trapped when a dropdown is open. pointer-events: auto
           * overrides the inherited pointer-events: none from CenteredDialog, ensuring
           * dropdown content (search input, list items, etc.) is interactive.
           */}
          <div ref={setPortalContainer} style={{ pointerEvents: 'auto' }} />
        </FocusScope>
      </CenteredDialog>
    </Portal>
  );
};

/**
 * Invisible overlay that captures clicks outside the modal to trigger onClickOutside.
 * During 'exiting' state, pointer-events are disabled so clicks pass through to the
 * page beneath, preventing the modal from blocking interactions while animating out.
 */
const Overlay = styled.div<{ $state: TransitionState }>`
  position: fixed;
  inset: 0;
  pointer-events: ${({ $state }) => ($state !== 'exiting' ? 'auto' : 'none')};
`;

const CenteredDialog = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

let portalChildrenCount = 0;

const Portal = ({ children }: { children: React.ReactNode }) => {
  const targetRef = useRef<HTMLElement>(getPortalTarget());

  useEffect(() => {
    const target = targetRef.current;

    portalChildrenCount++;
    // Inside fullscreen, body is behind the backdrop; append to the fullscreen element instead.
    const container = document.fullscreenElement ?? document.body;
    container.appendChild(target);
    if (!target.matches(':popover-open')) {
      target.showPopover();
    }

    return () => {
      portalChildrenCount--;

      if (portalChildrenCount === 0) {
        hideTopLayerHost(target);
        target.remove();
      }
    };
  }, []);

  return createPortal(children, targetRef.current);
};

const tagName = 'modal-container';

const getPortalTarget = (): HTMLElement => {
  const existingTarget = document.querySelector<HTMLElement>(tagName);

  if (existingTarget) {
    return existingTarget;
  }

  // The UA popover styles are neutralised so `position: fixed; inset: 0` children
  // position against the viewport rather than the popover box. `pointer-events: none`
  // keeps the host itself out of hit testing while it spans the viewport; Overlay and
  // AnimatedDialog already opt back in, and AnimatedBackdrop is deliberately click-through.
  return createTopLayerHost(tagName);
};

const emptyKeyframes = keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 0;
    }
`;

// 1ms is required to ensure that the animation is triggered
const mountingAnimation = css`
  animation: ${emptyKeyframes} 1ms;
`;

const enteringDialogStyles = css`
  opacity: 1;
  transition-delay: ${transitionDelay}s;
  transform: translateY(0);
`;

const exitingDialogStyles = css`
  transform: translateY(-10px);
`;

const AnimatedDialog = styled.div.attrs({
  role: 'dialog',
  'aria-modal': 'true',
  'aria-labelledby': MODAL_HEADER_TITLE_ID,
  'aria-describedby': MODAL_BODY_ID,
})<{
  $state: TransitionState;
}>`
  opacity: 0;
  transition-duration: ${transitionDuration}s;
  transform: translateY(10px);
  pointer-events: auto;
  transition-property: opacity, transform;
  transition-delay: 0s;
  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }

  ${({ $state }) => $state === 'mounting' && mountingAnimation}
  ${({ $state }) => $state === 'entering' && enteringDialogStyles}
  ${({ $state }) => $state === 'exiting' && exitingDialogStyles}
`;

const enteringBackdropStyles = css`
  opacity: 1;
  transition-delay: 0s;
`;

const exitingBackdropStyles = css`
  /* Using 0.001 instead of 0 to ensure the browser triggers a transitionend event.
     If this matches the base opacity (0), the browser may skip the transition entirely. */
  opacity: 0.001;
`;

const AnimatedBackdrop = styled.div<{
  $state: TransitionState;
}>`
  opacity: 0;
  transition-duration: ${transitionDuration}s;
  position: fixed;
  inset: 0;
  transition-property: opacity;
  pointer-events: none;
  background-color: ${colors.bgOverlay};
  transition-delay: ${transitionDelay}s;
  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
  ${({ $state }) => $state === 'mounting' && mountingAnimation}
  ${({ $state }) => $state === 'entering' && enteringBackdropStyles}
  ${({ $state }) => $state === 'exiting' && exitingBackdropStyles}
`;

const useTopmostModalEscapeKey = (
  onEscPress: VoidFunction | undefined,
  containerSelector: string,
  dialogRef: React.RefObject<HTMLDivElement | null>
) => {
  const handleEscapeWithTopmostCheck = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !onEscPress || !dialogRef.current) {
      return;
    }

    // Only handle escape if this modal is the topmost one
    const allModals = Array.from(document.querySelectorAll('[role="dialog"]'));
    const topmostModal = allModals[allModals.length - 1];

    if (topmostModal !== dialogRef.current) {
      return;
    }

    return handleEscapeButtonPressed(onEscPress, containerSelector)(event);
  };

  useKeydown(handleEscapeWithTopmostCheck);
};
